import { apiFetch } from "@/lib/api";

export type DashboardConversionBreakdown = {
  total: number;
  converted: number;
  rate: number;
};

export type DashboardServiceMix = {
  tours: number;
  transfers: number;
  attractions: number;
  airport_services: number;
  additional_requests: number;
};

export type DashboardTopSpender = {
  name: string;
  total: number;
  count: number;
};

export type CeoDashboardResponse = {
  total_revenue: number;
  ytd_revenue: number;
  adjusted_period_revenue: number;
  avg_ticket: number;
  lost_value: number;
  overall_conversion: number;
  conversion_by_sales: Record<string, DashboardConversionBreakdown>;
  conversion_by_client_type: Record<string, DashboardConversionBreakdown>;
  service_mix: DashboardServiceMix;
  top_agencies: DashboardTopSpender[];
  top_operators: DashboardTopSpender[];
  top_final_clients: DashboardTopSpender[];
  total_proposals: number;
  converted_count: number;
  controllable_cancellations: number;
  uncontrollable_cancellations: number;
  uncontrollable_canceled_value: number;
  uncontrollable_cancellation_rate: number;
};

export type GmDashboardResponse = {
  today_proposals: number;
  next7_days: number;
  status_breakdown: Record<string, number>;
  status_columns: string[];
  owner_status_rows: Array<{
    owner_id: string | null;
    owner_name: string;
    status_breakdown: Record<string, number>;
    total_proposals: number;
  }>;
  board_columns: Array<{
    status: string;
    label: string;
    items: Array<{
      proposal_id: string;
      case_number: string;
      title?: string | null;
      client_name?: string | null;
      status: string;
      priority?: string | null;
      updated_at?: string | null;
      expiry_date?: string | null;
      status_age_business_days?: number | null;
      total_value?: number | null;
      trip_start_date?: string | null;
      owner_id?: string | null;
      owner_name?: string | null;
      stale_follow_up?: boolean | null;
      days_since_last_follow_up?: number | null;
    }>;
  }>;
  avg_creation_to_sent: number;
  avg_sent_to_approved: number;
  sla_compliance: number;
  overdue_proposals: number;
  rework: number;
  upcoming_proposals: any[];
  pending_proposals: any[];
};

export type StaffDashboardResponse = {
  active_cases_count: number;
  due_today_count: number;
  overdue_count: number;
  stale_count: number;
  needs_attention_count: number;
  board_columns: Array<{
    status: string;
    label: string;
    items: Array<{
      proposal_id: string;
      case_number: string;
      title?: string | null;
      client_name?: string | null;
      status: string;
      priority?: string | null;
      updated_at?: string | null;
      expiry_date?: string | null;
      status_age_business_days?: number | null;
      total_value?: number | null;
      trip_start_date?: string | null;
      owner_id?: string | null;
      owner_name?: string | null;
      stale_follow_up?: boolean | null;
      days_since_last_follow_up?: number | null;
    }>;
  }>;
  assigned_cases: any[];
};

export const getCeoDashboard = async (params: { from: string; to: string }): Promise<CeoDashboardResponse> => {
  const query = new URLSearchParams();
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);
  return apiFetch<CeoDashboardResponse>(`/dashboard/ceo?${query.toString()}`);
};

export const getGmDashboard = async (): Promise<GmDashboardResponse> => {
  return apiFetch<GmDashboardResponse>("/dashboard/gm");
};

export const getStaffDashboard = async (): Promise<StaffDashboardResponse> => {
  return apiFetch<StaffDashboardResponse>("/dashboard/staff");
};

export type WeeklySalesReportItem = {
  id: string;
  internal_reference?: string | null;
  title?: string | null;
  client_name?: string | null;
  owner_name?: string | null;
  status: string;
  currency?: string | null;
  value?: number | null;
  created_at?: string | null;
  last_follow_up_at?: string | null;
  last_follow_up_message?: string | null;
  days_since_last_follow_up?: number | null;
  cancellation_type?: "internal" | "external" | null;
  canceled_at?: string | null;
};

export type WeeklySalesReportSection = {
  count: number;
  total_value: number;
  items: WeeklySalesReportItem[];
};

export type WeeklySalesReportResponse = {
  week_start: string;
  week_end: string;
  generated_at: string;
  new_proposals: WeeklySalesReportSection;
  older_active_proposals: WeeklySalesReportSection;
  canceled: {
    count: number;
    total_value: number;
    internal: WeeklySalesReportSection;
    external: WeeklySalesReportSection;
  };
};

export const getWeeklySalesReport = async (week?: string): Promise<WeeklySalesReportResponse> => {
  const query = week ? `?week=${encodeURIComponent(week)}` : "";
  return apiFetch<WeeklySalesReportResponse>(`/dashboard/weekly-sales-report${query}`);
};
